import type { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, Badge, Button, Card, Chip, DataTable, type DataTableColumn } from '@zarya/ui';
import { Icon } from '@zarya/theme';
import type { components } from '@app/api/types';
import { t } from '@app/i18n';
import { identityAccessHref } from '@app/navigation';
import { RISK_TOM, riskLabel } from '@app/risk';
import { useMultiSite } from '@app/main/Sites/Sites.api';
import { ReadingNote, readingView } from '@app/reading';
import { ToolName } from '@app/shared/ToolName';
import { useIdentityCapabilities } from '../Identities.api';
import { TabState } from './TabState';
import s from '../Identities.module.css';

/**
 * Aba **Capabilities** de T5 (FR-054): uma tabela só (aim-ypw, decisões do mrolim de 11/10/2026), no lugar dos cards
 * «Aprovadas» e «Conferência com o PDP». Cada linha é uma tool, ou um agente chamado, que está em pelo menos uma de
 * três colunas:
 *
 *  - **Aprovada no contrato** — o contrato em vigor, a autoridade que o produto guarda;
 *  - **Concedida no PDP** — a foto da conferência com o PDP, de quando ela foi lida (cinza quando não foi lida);
 *  - **Declarada na release** — o que a imagem da release do contrato em vigor declara.
 *
 * Ler as três lado a lado é o ponto: «a imagem pede e o contrato não dá», «o contrato dá e o PDP ainda não», «o PDP dá
 * sem contrato». O card «Declaradas na release pendente» continua em cima, só com pendência.
 *
 * Formato genérico `resource.tool` — o mesmo que o `agent.yaml` usa para declarar.
 */

type Capability = components['schemas']['Capability'];
type AgentRef = components['schemas']['AgentRef'];

/**
 * A frase do formato, com o exemplo em `<code>` **dentro** dela.
 *
 * O marcador `{sample}` fica no meio de uma frase única no catálogo em vez de a
 * frase ser partida em duas chaves: partir obriga o tradutor a manter a ordem do
 * português, e é exatamente a ordem que muda entre idiomas. Aqui o texto é uma
 * frase inteira nos dois locales e a marcação entra onde cada um a colocou.
 */
function FormatoDaDeclaracao() {
  const [antes, depois] = t('identities.capabilitiesTab.format').split('{sample}');
  return (
    <>
      {antes}
      <code>{t('identities.capabilitiesTab.format.sample')}</code>
      {depois}
    </>
  );
}

/** Sim, não, ou não lido: `null` é o PDP que não foi lido (ou a release sem declaração), nunca «não». */
type YesNo = boolean | null;

/**
 * Uma linha da tabela. `capability` é a descrição mais rica que se tem da tool (a do contrato, senão a da declaração,
 * senão só o nome que o PDP dá); `agent`, a do agente chamado numa delegação.
 */
interface Row {
  key: string;
  capability?: Capability;
  agent?: { name: string; path: string; external: boolean };
  approved: boolean;
  granted: YesNo;
  declared: YesNo;
}

/**
 * Monta as linhas a partir das três fontes. A chave de uma tool é o par servidor/nome (decisão 13), a de uma delegação
 * é o caminho do agente. O PDP só fala de tools e pelo NOME, então a coluna dele casa pelo nome da tool, e numa
 * delegação ela fica «não lido»: a conferência com o PDP não lê agentes.
 */
function rowsOf(payload: components['schemas']['IdentityCapabilities'] | undefined): Row[] {
  const pdp = payload?.pdp;
  const declared = payload?.declared;
  const grantedNames = new Set(pdp?.granted_in_pdp ?? []);
  const toolGranted = (name: string | undefined): YesNo => (pdp ? grantedNames.has(name ?? '') : null);
  const notDeclared: YesNo = declared ? false : null;

  const tools = new Map<string, Row>();
  const toolKey = (c: Capability) => `tool:${c.resource_gateway_id ?? ''}/${c.tool_name ?? ''}`;
  for (const c of payload?.approved ?? []) {
    tools.set(toolKey(c), {
      key: toolKey(c),
      capability: c,
      approved: true,
      granted: toolGranted(c.tool_name),
      declared: notDeclared,
    });
  }
  for (const c of declared?.capabilities ?? []) {
    const row = tools.get(toolKey(c));
    if (row) {
      row.declared = true;
      continue;
    }
    tools.set(toolKey(c), {
      key: toolKey(c),
      capability: c,
      approved: false,
      granted: toolGranted(c.tool_name),
      declared: true,
    });
  }
  // O que o PDP concede e nenhuma das outras duas cita: só o nome, que é o que o PDP dá.
  const namesInRows = new Set([...tools.values()].map((r) => r.capability?.tool_name));
  for (const name of pdp?.granted_in_pdp ?? []) {
    if (namesInRows.has(name)) continue;
    tools.set(`pdp:${name}`, {
      key: `pdp:${name}`,
      capability: { tool_name: name },
      approved: false,
      granted: true,
      declared: notDeclared,
    });
  }
  const sortedTools = [...tools.values()].sort((a, b) =>
    `${a.capability?.resource_gateway_id ?? ''}/${a.capability?.tool_name ?? ''}`.localeCompare(
      `${b.capability?.resource_gateway_id ?? ''}/${b.capability?.tool_name ?? ''}`,
    ),
  );

  const agents = new Map<string, Row>();
  for (const ref of payload?.a2a_approved ?? []) {
    agents.set(`agent:${ref.path}`, {
      key: `agent:${ref.path}`,
      agent: agentOf(ref),
      approved: true,
      granted: null,
      declared: notDeclared,
    });
  }
  for (const path of declared?.a2a ?? []) {
    const row = agents.get(`agent:${path}`);
    if (row) {
      row.declared = true;
      continue;
    }
    agents.set(`agent:${path}`, {
      key: `agent:${path}`,
      agent: { name: path, path, external: false },
      approved: false,
      granted: null,
      declared: true,
    });
  }
  return [...sortedTools, ...agents.values()];
}

function agentOf(ref: AgentRef): Row['agent'] {
  return { name: ref.name || ref.path, path: ref.path, external: ref.scope === 'external' };
}

/**
 * O valor de sim/não, em ícone: círculo verde com visto, círculo vermelho com X, cinza quando não foi lido. O nome
 * acessível sai do catálogo, porque a cor e o desenho sozinhos não dizem nada a um leitor de tela.
 */
function YesNoIcon({ value, column }: { value: YesNo; column: 'approved' | 'granted' | 'declared' }) {
  const state = value === true ? 'yes' : value === false ? 'no' : 'unread';
  return (
    <span
      className={s.simNao}
      data-testid={`capability-${column}`}
      data-state={state}
      role="img"
      aria-label={t(`identities.capabilitiesTab.${state}`)}
    >
      {value === null ? null : <Icon name={value ? 'check' : 'close'} size="xs" />}
    </span>
  );
}

/** O título de uma coluna de sim/não, estreito: uma palavra por linha, em qualquer idioma. */
function NarrowHeader({ children }: { children: ReactNode }) {
  return <span className={s.cabecalhoEstreito}>{children}</span>;
}

/** A célula da tool: o nome como T7 e T10 o mostram, com o limite e, com mais de um site, o site. */
function ToolCell({ row, multiSite }: { row: Row; multiSite: boolean }) {
  if (row.agent) {
    return (
      <span className={s.capItem}>
        <Chip icon="exchange">invoke_agent → {row.agent.name}</Chip>
        {row.agent.external ? <Badge tone="neutral">{t('identities.capabilitiesTab.external')}</Badge> : null}
      </span>
    );
  }
  const capability = row.capability ?? {};
  const limits = Object.entries(capability.constraints ?? {});
  const trailing = (
    <>
      {limits.map(([parametro, valor]) => (
        <Chip key={parametro} icon="lock">
          {parametro} ≤ {valor}
        </Chip>
      ))}
      {/*
        O SITE do resource que publica a tool (A2, 11/09), com o prefixo «site:» dentro do chip: numa célula de tool
        o slug solto não se distingue de mais um atributo dela. Só com mais de um site, como em T4/T6/T14.
      */}
      {multiSite && capability.site_slug ? (
        <Chip>{t('identities.capabilitiesTab.site', { site: capability.site_slug })}</Chip>
      ) : null}
    </>
  );
  return (
    <span className={`${s.capItem} ${s.capTool}`} data-testid="capability-tool">
      <ToolName
        displayName={capability.tool_display_name}
        technicalName={capability.tool_name ?? ''}
        trailing={trailing}
      />
    </span>
  );
}

/**
 * Uma capability de tool da release pendente, uma por linha (aim-92c), com o `+` na frente: o card de cima, que a
 * tabela única não mudou (decisão 2). A tool como T7 e T10 a mostram, com o limite, o site com mais de um, e o risco.
 */
function RequestedChip({ capability }: { capability: Capability }) {
  const limits = Object.entries(capability.constraints ?? {});
  const multiSite = useMultiSite();
  const trailing = (
    <>
      {limits.map(([parametro, valor]) => (
        <Chip key={parametro} icon="lock">
          {parametro} ≤ {valor}
        </Chip>
      ))}
      {multiSite && capability.site_slug ? (
        <Chip>{t('identities.capabilitiesTab.site', { site: capability.site_slug })}</Chip>
      ) : null}
      <Badge tone={RISK_TOM[capability.risk ?? 'desconhecido']}>{riskLabel(capability.risk ?? 'desconhecido')}</Badge>
    </>
  );
  return (
    <span className={`${s.capItem} ${s.capTool}`} data-testid="capability-tool">
      <Badge tone="primary">+</Badge>
      <ToolName
        displayName={capability.tool_display_name}
        technicalName={capability.tool_name ?? ''}
        trailing={trailing}
      />
    </span>
  );
}

export interface CapabilitiesTabProps {
  identityId: string;
  ativa: boolean;
  /** A gaveta pediu a leitura da identidade e o pedido não voltou: a conferência já diz «lendo». */
  readingPending?: boolean;
}

export function CapabilitiesTab({ identityId, ativa, readingPending = false }: CapabilitiesTabProps) {
  const navigate = useNavigate();
  const capabilities = useIdentityCapabilities(identityId, ativa);
  const payload = capabilities.data;
  const pedidas = payload?.requested;
  const pdp = payload?.pdp;
  // A conferência com o PDP sai da foto do L9 (aim-6ph.13, D15): a nota diz de quando ela é, «o que o PDP concedia às
  // 14:05», em vez de «o que o PDP concede hoje», que a foto não pode afirmar.
  const pdpReading = payload?.pdp_reading;
  const pdpOptions = { family: 'pdp' as const, pending: readingPending, form: 'at' as const };
  const pdpView = readingView(pdpReading, pdpOptions);
  // Nunca lido, e lendo agora (D): a nota diz «ainda não lido · lendo o PDP…» em cima da tabela.
  const pdpFirst = !pdp && pdpView?.state === 'D' && (Boolean(pdpReading?.in_flight) || readingPending);
  const multiSite = useMultiSite();
  const rows = rowsOf(payload);

  const columns: DataTableColumn<Row>[] = [
    {
      key: 'tool',
      header: t('identities.capabilitiesTab.column.tool'),
      cell: (row) => <ToolCell row={row} multiSite={multiSite} />,
    },
    {
      key: 'risk',
      header: t('identities.capabilitiesTab.column.risk'),
      // Rótulo e tom vêm de `@app/risk`, a fonte única. Numa delegação não há risco de tool a mostrar.
      cell: (row) =>
        row.agent ? (
          <span className={s.vazio}>—</span>
        ) : (
          <Badge tone={RISK_TOM[row.capability?.risk ?? 'desconhecido']}>
            {riskLabel(row.capability?.risk ?? 'desconhecido')}
          </Badge>
        ),
    },
    {
      key: 'approved',
      header: <NarrowHeader>{t('identities.capabilitiesTab.column.approved')}</NarrowHeader>,
      cell: (row) => <YesNoIcon value={row.approved} column="approved" />,
    },
    {
      key: 'granted',
      header: <NarrowHeader>{t('identities.capabilitiesTab.column.granted')}</NarrowHeader>,
      cell: (row) => <YesNoIcon value={row.granted} column="granted" />,
    },
    {
      key: 'declared',
      header: <NarrowHeader>{t('identities.capabilitiesTab.column.declared')}</NarrowHeader>,
      cell: (row) => <YesNoIcon value={row.declared} column="declared" />,
    },
  ];

  return (
    <>
      {/* O atalho para onde as capabilities se editam (aim-vmx, protótipo aprovado pelo mrolim em 11/10/2026): o
          mesmo caminho do botão de Security Contracts (T13, `ContractStateCard`). Fica fora do `TabState`, porque não
          depende da leitura. Todos os papéis o veem: quem decide quem salva é Identity Access. */}
      <div className={s.tabActions}>
        <Button
          variant="out"
          size="sm"
          icon="key"
          data-testid="capabilities-edit-in-identity-access"
          onClick={() => navigate(identityAccessHref(identityId))}
        >
          {t('identities.capabilitiesTab.editInIdentityAccess')}
        </Button>
      </div>
      <TabState
        carregando={capabilities.isLoading}
        erro={capabilities.isError ? capabilities.error : null}
        tituloErro={t('identities.capabilitiesTab.error')}
        onRetry={() => void capabilities.refetch()}
        rotulo={t('identities.capabilitiesTab.loading')}
      >
        <Alert tone="info">
          <FormatoDaDeclaracao />
        </Alert>

        {pedidas ? (
          <Card
            title={t('identities.capabilitiesTab.requested.title')}
            subtitle={pedidas.release_digest?.slice(0, 19)}
            className={s.cardEmpilhado}
            toolbar={
              pedidas.approval_id ? (
                <Link className={s.link} to={`/aprovacoes?drawer=${pedidas.approval_id}`}>
                  {t('identities.capabilitiesTab.requested.seeApproval')}
                </Link>
              ) : null
            }
          >
            <div className={s.capLista}>
              {(pedidas.capabilities ?? []).map((capability) => (
                <RequestedChip key={capability.tool_id} capability={capability} />
              ))}
              {(pedidas.a2a ?? []).map((slug) => (
                <span className={s.capItem} key={slug}>
                  <Badge tone="primary">+</Badge>
                  <Chip icon="exchange">invoke_agent → {slug}</Chip>
                </span>
              ))}
            </div>
          </Card>
        ) : null}

        <Card
          title={
            payload?.contract_version != null
              ? t('identities.capabilitiesTab.table.titleWithVersion', { version: payload.contract_version })
              : t('identities.capabilitiesTab.table.title')
          }
          subtitle={
            pdpFirst ? (
              <ReadingNote reading={pdpReading} {...pdpOptions} testId="pdp-check-reading" />
            ) : pdp && pdpView?.text ? (
              <ReadingNote
                reading={pdpReading}
                {...pdpOptions}
                compose={(reading) => t('identities.capabilitiesTab.pdp.subtitle', { reading })}
                testId="pdp-check-reading"
              />
            ) : undefined
          }
          tip={t('identities.capabilitiesTab.table.tip')}
          className={s.cardEmpilhado}
        >
          {!pdp && !pdpFirst && payload?.pdp_error_code ? (
            <Alert tone="warning" title={t('identities.capabilitiesTab.pdp.unavailable.title')}>
              {t('identities.capabilitiesTab.pdp.unavailable.message', { code: payload.pdp_error_code })}
            </Alert>
          ) : null}
          <DataTable<Row>
            label={t('identities.capabilitiesTab.table.label')}
            columns={columns}
            rows={rows}
            rowKey={(row) => row.key}
            emptyIcon="shield"
            emptyTitle={t('identities.capabilitiesTab.approved.empty')}
          />
        </Card>
      </TabState>
    </>
  );
}
